import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import {
  FaCrown, FaWallet, FaLock, FaEnvelope,
  FaCalendarAlt, FaLink, FaChevronRight,
  FaSlidersH, FaHistory,
} from 'react-icons/fa';
import { getRequest, patchRequest } from '../../services/api/apiService';
import { useLang } from '../../i18n/LanguageContext';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';

const Section = ({ title, children }) => {
  const { t } = useLang();
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <h2 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-5">{t(title)}</h2>
      {children}
    </div>
  );
};

const Profile = () => {
  const [user, setUser] = useState(null);
  const [billing, setBilling] = useState(null);
  const [name, setName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    Promise.all([
      getRequest('/api/auth/user-info'),
      getRequest('/api/payment/status').catch(() => null),
    ]).then(([userData, billingData]) => {
      setUser(userData);
      setName(userData.name || '');
      setBilling(billingData);
    }).catch(() => toast.error('Échec du chargement du profil.'));
  }, []);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Le nom ne peut pas être vide.');
    setSavingProfile(true);
    try {
      await patchRequest('/api/auth/profile', { name: name.trim() });
      toast.success('Nom mis à jour.');
      setUser((u) => ({ ...u, name: name.trim() }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la mise à jour.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) return toast.error('Mot de passe actuel requis.');
    if (newPassword.length < 4) return toast.error('Le nouveau mot de passe doit faire au moins 4 caractères.');
    if (newPassword !== confirmPassword) return toast.error('Les mots de passe ne correspondent pas.');
    setSavingPassword(true);
    try {
      await patchRequest('/api/auth/profile', { currentPassword, newPassword });
      toast.success('Mot de passe mis à jour.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors du changement de mot de passe.');
    } finally {
      setSavingPassword(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <p className="text-gray-400 text-sm">Chargement du profil…</p>
      </div>
    );
  }

  const initials = user.name
    ? user.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  const isGoogle = !!user.google_id;
  const isPremium = billing?.isPremium || user.ispremium;
  const walletBalance = billing?.walletBalance ?? parseFloat(user.wallet_balance || 0).toFixed(2);

  return (
    <div className="p-3 sm:p-6 max-w-2xl mx-auto space-y-5">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Header — empilé sur mobile, en ligne dès sm ── */}
      <div className="bg-[#221C51] rounded-2xl p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 shadow">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-xl sm:text-2xl font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0 sm:hidden">
            <p className="text-lg font-bold text-white truncate">{user.name}</p>
            <p className="text-xs text-white/70 flex items-center gap-1 truncate mt-0.5">
              <FaEnvelope className="flex-shrink-0" /> {user.email}
            </p>
          </div>
        </div>
        <div className="flex-1 min-w-0 hidden sm:block">
          <p className="text-xl font-bold text-white truncate">{user.name}</p>
          <p className="text-sm text-white/70 flex items-center gap-1 truncate mt-0.5">
            <FaEnvelope className="flex-shrink-0" /> {user.email}
          </p>
          <p className="text-xs text-white/50 flex items-center gap-1 mt-1">
            <FaCalendarAlt className="flex-shrink-0" /> Membre depuis le {memberSince}
          </p>
        </div>
        <div className="flex sm:flex-col items-center sm:items-end gap-2 flex-shrink-0 flex-wrap">
          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${isPremium ? 'bg-yellow-400 text-yellow-900' : 'bg-white/20 text-white'}`}>
            <FaCrown /> {isPremium ? 'Premium' : 'Free'}
          </span>
          <span className="flex items-center gap-1 px-3 py-1 bg-white/10 text-white rounded-full text-xs font-semibold">
            <FaWallet /> €{walletBalance}
          </span>
          <span className="sm:hidden text-[11px] text-white/50 flex items-center gap-1 basis-full">
            <FaCalendarAlt className="flex-shrink-0" /> Membre depuis le {memberSince}
          </span>
        </div>
      </div>

      {/* ── Langue de l'interface ── */}
      <Section title="Langue">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-gray-600">🌐 Français · English · العربية</p>
          <LanguageSwitcher />
        </div>
      </Section>

      {/* ── Account Settings ── */}
      <Section title="Informations du compte">
        <form onSubmit={handleSaveName} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Nom complet</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
            <input
              type="text"
              value={user.email}
              disabled
              className="w-full border border-gray-100 rounded-xl px-4 py-2.5 text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-400 mt-1">L'adresse e-mail ne peut pas être modifiée.</p>
          </div>
          <button
            type="submit"
            disabled={savingProfile}
            className="bg-[#221C51] text-white text-sm px-6 py-2.5 rounded-xl hover:bg-[#2d2470] transition disabled:opacity-50"
          >
            {savingProfile ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </form>
      </Section>

      {/* ── Security / Password ── */}
      <Section title="Sécurité">
        {isGoogle ? (
          <div className="flex items-start gap-3 text-sm text-gray-500">
            <FaLock className="mt-0.5 text-gray-400 flex-shrink-0" />
            <p>Votre compte est lié à Google. La gestion du mot de passe se fait via Google.</p>
          </div>
        ) : (
          <form onSubmit={handleSavePassword} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Mot de passe actuel</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Confirmer</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={savingPassword}
              className="bg-[#221C51] text-white text-sm px-6 py-2.5 rounded-xl hover:bg-[#2d2470] transition disabled:opacity-50"
            >
              {savingPassword ? 'Mise à jour…' : 'Changer le mot de passe'}
            </button>
          </form>
        )}
      </Section>

      {/* ── Plan & Billing ── */}
      <Section title="Mon abonnement">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isPremium ? 'bg-yellow-100' : 'bg-gray-100'}`}>
              <FaCrown className={isPremium ? 'text-yellow-500' : 'text-gray-400'} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-[#221C51] text-sm">{isPremium ? 'Premium' : 'Plan Free'}</p>
              <p className="text-xs text-gray-400">{isPremium ? 'Toutes les fonctionnalités débloquées' : 'Passez à Premium pour tout débloquer'}</p>
            </div>
          </div>
          <Link
            to="/dashboard/billing"
            className="text-xs font-semibold text-[#221C51] border border-[#221C51] px-4 py-2 rounded-xl hover:bg-[#221C51] hover:text-white transition flex-shrink-0"
          >
            {isPremium ? 'Gérer' : 'Passer Premium'}
          </Link>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <FaWallet className="text-[#221C51] flex-shrink-0" />
            <span className="text-sm text-gray-600">Budget publicitaire (wallet)</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-lg font-black text-[#221C51]">€{walletBalance}</span>
            <Link to="/dashboard/billing" className="text-xs text-[#221C51] underline">Recharger</Link>
          </div>
        </div>
      </Section>

      {/* ── Quick Links ── */}
      <Section title="Accès rapide">
        <div className="space-y-1">
          {[
            { to: '/dashboard/connected-platforms', icon: <FaLink />, label: 'Plateformes connectées', desc: 'Gérer Meta, TikTok, Google…' },
            { to: '/dashboard/ads-ruleset', icon: <FaSlidersH />, label: 'Règles automatiques', desc: 'Configurer vos règles de campagne' },
            { to: '/dashboard/billing', icon: <FaWallet />, label: 'Facturation', desc: 'Abonnement, wallet, historique' },
            { to: '/dashboard/ads-history', icon: <FaHistory />, label: 'Historique des publicités', desc: 'Voir toutes vos campagnes créées' },
          ].map(({ to, icon, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition group"
            >
              <div className="w-8 h-8 rounded-lg bg-[#221C51]/10 flex items-center justify-center text-[#221C51] text-sm">
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#221C51]">{label}</p>
                <p className="text-xs text-gray-400">{desc}</p>
              </div>
              <FaChevronRight className="text-gray-300 group-hover:text-[#221C51] transition text-xs" />
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default Profile;
